import { create } from 'zustand';
import {
  getDashboardStats,
  getTopCourses,
  getTasks,
  getLearningHoursData,
  getLearningProgress,
  performTaskAction,
  DashboardStat,
  CourseProgress,
  Task,
  LearningHoursData,
  LearningProgress,
  DateRange,
  TaskActionType,
} from '@/services/PagesServices/AdminDashboardService';

interface AdminDashboardState {
  // Data states
  stats: DashboardStat[] | null;
  courses: CourseProgress[] | null;
  tasks: Task[] | null;
  learningHoursData: LearningHoursData[] | null;
  learningProgress: LearningProgress | null;

  // Date range state
  dateRange: DateRange;

  // Granular loading states
  loadingStats: boolean;
  loadingCourses: boolean;
  loadingTasks: boolean;
  loadingLearningHours: boolean;
  loadingProgress: boolean;

  // Task action loading states (per task)
  taskActionLoading: Record<string, boolean>;

  // Granular error states
  errorStats: string | null;
  errorCourses: string | null;
  errorTasks: string | null;
  errorLearningHours: string | null;
  errorProgress: string | null;

  // Actions
  fetchStats: () => Promise<void>;
  fetchCourses: () => Promise<void>;
  fetchTasks: () => Promise<void>;
  fetchLearningHoursData: () => Promise<void>;
  fetchLearningProgress: () => Promise<void>;
  fetchAllData: () => Promise<void>;
  setDateRange: (startDate: Date, endDate: Date) => void;
  handleTaskAction: (taskId: string, action: TaskActionType) => Promise<{ success: boolean; message?: string }>;
  removeTask: (taskId: string) => void;
}

// Default date range: last 7 days
const getDefaultDateRange = (): DateRange => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 7);
  return { startDate, endDate };
};

const useAdminDashboardStore = create<AdminDashboardState>((set, get) => ({
  // Initial data states
  stats: null,
  courses: null,
  tasks: null,
  learningHoursData: null,
  learningProgress: null,

  // Initial date range
  dateRange: getDefaultDateRange(),

  // Initial loading states
  loadingStats: false,
  loadingCourses: false,
  loadingTasks: false,
  loadingLearningHours: false,
  loadingProgress: false,

  // Task action loading
  taskActionLoading: {},

  // Initial error states
  errorStats: null,
  errorCourses: null,
  errorTasks: null,
  errorLearningHours: null,
  errorProgress: null,

  fetchStats: async () => {
    const { dateRange } = get();
    console.log('🏪 AdminDashboardStore: fetchStats called');
    set({ loadingStats: true, errorStats: null });

    try {
      const response = await getDashboardStats(dateRange);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to load stats');
      }

      set({
        stats: response.data ?? [],
        loadingStats: false,
      });
      console.log('🏪 AdminDashboardStore: Stats loaded successfully');
    } catch (err: any) {
      console.error('🏪 AdminDashboardStore: Error in fetchStats:', err);
      set({
        errorStats: err.message ?? 'Something went wrong',
        loadingStats: false,
      });
    }
  },

  fetchCourses: async () => {
    const { dateRange } = get();
    console.log('🏪 AdminDashboardStore: fetchCourses called');
    set({ loadingCourses: true, errorCourses: null });

    try {
      const response = await getTopCourses(dateRange);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to load courses');
      }

      set({
        courses: response.data ?? [],
        loadingCourses: false,
      });
      console.log('🏪 AdminDashboardStore: Courses loaded successfully');
    } catch (err: any) {
      console.error('🏪 AdminDashboardStore: Error in fetchCourses:', err);
      set({
        errorCourses: err.message ?? 'Something went wrong',
        loadingCourses: false,
      });
    }
  },

  fetchTasks: async () => {
    const { dateRange } = get();
    console.log('🏪 AdminDashboardStore: fetchTasks called');
    set({ loadingTasks: true, errorTasks: null });

    try {
      const response = await getTasks(dateRange);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to load tasks');
      }

      set({
        tasks: response.data ?? [],
        loadingTasks: false,
      });
      console.log('🏪 AdminDashboardStore: Tasks loaded successfully');
    } catch (err: any) {
      console.error('🏪 AdminDashboardStore: Error in fetchTasks:', err);
      set({
        errorTasks: err.message ?? 'Something went wrong',
        loadingTasks: false,
      });
    }
  },

  fetchLearningHoursData: async () => {
    const { dateRange } = get();
    console.log('🏪 AdminDashboardStore: fetchLearningHoursData called');
    set({ loadingLearningHours: true, errorLearningHours: null });

    try {
      const response = await getLearningHoursData(dateRange);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to load learning hours');
      }

      set({
        learningHoursData: response.data ?? [],
        loadingLearningHours: false,
      });
      console.log('🏪 AdminDashboardStore: Learning hours loaded successfully');
    } catch (err: any) {
      console.error('🏪 AdminDashboardStore: Error in fetchLearningHoursData:', err);
      set({
        errorLearningHours: err.message ?? 'Something went wrong',
        loadingLearningHours: false,
      });
    }
  },

  fetchLearningProgress: async () => {
    const { dateRange } = get();
    console.log('🏪 AdminDashboardStore: fetchLearningProgress called');
    set({ loadingProgress: true, errorProgress: null });

    try {
      const response = await getLearningProgress(dateRange);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to load learning progress');
      }

      set({
        learningProgress: response.data ?? null,
        loadingProgress: false,
      });
      console.log('🏪 AdminDashboardStore: Learning progress loaded successfully');
    } catch (err: any) {
      console.error('🏪 AdminDashboardStore: Error in fetchLearningProgress:', err);
      set({
        errorProgress: err.message ?? 'Something went wrong',
        loadingProgress: false,
      });
    }
  },

  fetchAllData: async () => {
    console.log('🏪 AdminDashboardStore: fetchAllData called');
    const {
      fetchStats,
      fetchCourses,
      fetchTasks,
      fetchLearningHoursData,
      fetchLearningProgress,
    } = get();

    // Fetch all data in parallel
    await Promise.all([
      fetchStats(),
      fetchCourses(),
      fetchTasks(),
      fetchLearningHoursData(),
      fetchLearningProgress(),
    ]);
  },

  setDateRange: (startDate: Date, endDate: Date) => {
    console.log('🏪 AdminDashboardStore: setDateRange called', { startDate, endDate });

    // Update date range
    set({ dateRange: { startDate, endDate } });

    // Clear all data to trigger refetch
    set({
      stats: null,
      courses: null,
      tasks: null,
      learningHoursData: null,
      learningProgress: null,
    });

    // Refetch all data with new date range
    get().fetchAllData();
  },

  handleTaskAction: async (taskId: string, action: TaskActionType) => {
    console.log(`🏪 AdminDashboardStore: handleTaskAction called - ${action} on ${taskId}`);

    // Set loading state for this specific task
    set((state) => ({
      taskActionLoading: { ...state.taskActionLoading, [taskId]: true },
    }));

    try {
      const response = await performTaskAction(taskId, action);

      // Clear loading state
      set((state) => ({
        taskActionLoading: { ...state.taskActionLoading, [taskId]: false },
      }));

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? `Failed to ${action} task`,
        };
      }

      // If action was successful and it's approve or reject, remove task from list
      if (action === 'approve' || action === 'reject') {
        get().removeTask(taskId);
      }

      return {
        success: true,
        message: response.data?.message ?? `Task ${action}ed successfully`,
      };
    } catch (err: any) {
      console.error(`🏪 AdminDashboardStore: Error in handleTaskAction:`, err);

      // Clear loading state
      set((state) => ({
        taskActionLoading: { ...state.taskActionLoading, [taskId]: false },
      }));

      return {
        success: false,
        message: err.message ?? 'Something went wrong',
      };
    }
  },

  removeTask: (taskId: string) => {
    set((state) => ({
      tasks: state.tasks?.filter((task) => task.id !== taskId) ?? null,
    }));
  },
}));

export default useAdminDashboardStore;
